/**
 * passagesFiltres — trouver, dans un paragraphe lu, le texte qui a DÉJÀ une
 * forme donnée : le filtre `onlyWhere` de `format_text` (#1865, spec #1858).
 *
 * WHY par le lecteur de forme : « passe tout le gras en italique » doit prendre
 * le gras porté par un style nommé — de paragraphe, de caractère, de tableau —
 * comme le gras posé à la main. Les lectures de l'éditeur ne voient que la
 * forme directe ; le lecteur de forme résout la cascade que l'écran peint.
 *
 * Module pur : il ne connaît ni l'éditeur ni le navigateur. Ce que chaque
 * valeur de filtre veut dire pour un réglage est dit par `filtrerLeReglage`
 * (`reglagesDeTexte.ts`).
 *
 * ## Ce qu'est un « passage » pour le filtre
 *
 * La plus longue suite de texte du paragraphe qui a le même sort face au
 * filtre : deux passages du lecteur qui se touchent et le satisfont tous deux
 * n'en font qu'un, quelles que soient leurs autres formes ; de même pour deux
 * passages voisins où il ne s'évalue pas. C'est ce que l'avocat appelle
 * « un passage en gras », et c'est une étape de moins au plan.
 *
 * Une plage est située par sa POSITION dans le texte du paragraphe tel que le
 * lecteur le rend (cumul des longueurs des passages), jamais par son seul
 * texte : deux passages gras de même texte dans un paragraphe sont deux plages.
 */

import { CANVAS_TEXT_FORMAT_SETTING_KEYS, type CanvasTextFormatFilter, type CanvasTextFormatSettingKey } from '@hector/shared-types';
import type { FormeDeParagraphe, FormeDePassage } from '@hector/docx-forme';
import { filtrerLeReglage, passageLuPour } from './reglagesDeTexte';
import type { PlageDeTexte } from './verificationDeForme';

export interface PassagesFiltres {
  /** Les passages qui satisfont le filtre, dans l'ordre du paragraphe, sans chevauchement ni contact. */
  plages: PlageDeTexte[];
  /**
   * Passages où le filtre n'a pas pu être évalué : un de ses réglages ne s'y
   * lit pas, et aucun autre ne l'exclut. Ils ne sont pas trouvés ; les compter
   * permet à l'agent de le dire. Même unité que `plages` : une suite de texte
   * d'un tenant compte pour un.
   */
  nonEvalues: number;
  /** Les réglages du filtre qui ne se lisent pas sur ces passages, dans l'ordre du contrat. */
  reglagesNonLus: CanvasTextFormatSettingKey[];
}

/**
 * Un passage satisfait le filtre si TOUS ses réglages le font ; un seul qui ne
 * le fait pas l'exclut, lisible ou non pour les autres. Sinon, les réglages qui
 * ne se lisent pas sont rendus : le filtre n'y est pas évaluable.
 */
function verdictDuPassage(passage: FormeDePassage, filtre: CanvasTextFormatFilter): 'satisfait' | 'non_satisfait' | CanvasTextFormatSettingKey[] {
  const nonLus: CanvasTextFormatSettingKey[] = [];
  for (const reglage of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
    const attendu = filtre[reglage];
    if (attendu === undefined) continue;
    const lu = filtrerLeReglage(reglage, attendu, passageLuPour(passage, reglage));
    if (lu === 'non_satisfait') return 'non_satisfait';
    if (lu === 'non_evaluable') nonLus.push(reglage);
  }
  return nonLus.length === 0 ? 'satisfait' : nonLus;
}

/**
 * Les passages d'un paragraphe qui satisfont `filtre`.
 *
 * @param filtre Les valeurs attendues, telles que le contrat partagé les admet (`onlyWhere`).
 * @param dans Restreint la recherche à cette étendue du texte du paragraphe
 *   (cible « passage ») : seule la part des passages qu'elle recouvre compte.
 */
export function passagesQuiSatisfont(
  paragraphe: FormeDeParagraphe,
  filtre: CanvasTextFormatFilter,
  dans?: { debut: number; fin: number },
): PassagesFiltres {
  const plages: PlageDeTexte[] = [];
  const nonLus = new Set<CanvasTextFormatSettingKey>();
  let nonEvalues = 0;
  // Où finit le dernier passage non évalué : celui qui le touche le prolonge.
  let finDuDernierNonEvalue = -1;
  let curseur = 0;
  for (const passage of paragraphe.passages) {
    const debut = Math.max(curseur, dans?.debut ?? curseur);
    const fin = Math.min(curseur + passage.texte.length, dans?.fin ?? Number.POSITIVE_INFINITY);
    const texte = passage.texte.slice(debut - curseur, fin - curseur);
    curseur += passage.texte.length;
    if (debut >= fin) continue;
    const verdict = verdictDuPassage(passage, filtre);
    if (verdict === 'non_satisfait') continue;
    if (verdict !== 'satisfait') {
      for (const reglage of verdict) nonLus.add(reglage);
      if (finDuDernierNonEvalue !== debut) nonEvalues += 1;
      finDuDernierNonEvalue = fin;
      continue;
    }
    const derniere = plages.at(-1);
    if (derniere !== undefined && derniere.fin === debut) {
      derniere.fin = fin;
      derniere.texte += texte;
    } else plages.push({ debut, fin, texte });
  }
  return { plages, nonEvalues, reglagesNonLus: CANVAS_TEXT_FORMAT_SETTING_KEYS.filter((reglage) => nonLus.has(reglage)) };
}
